import { css, useTheme } from '@emotion/react';
import { Pause, Play, SkipBack, SkipForward } from 'lucide-react';

import type { MicDevice, MusicDevice, SessionSettings } from '@/hooks/use-sync-session';
import { fontSizes, spacing } from '@/theme/tokens';

export interface SessionTransportControlsProps {
  isPlaying: boolean;
  currentSection: number;
  sectionCount: number;
  onPlayPause: () => void;
  onPrevSection: () => void;
  onNextSection: () => void;
  participants: number;
  sessionSettings: SessionSettings;
  onMusicDeviceChange: (device: MusicDevice) => void;
  onMicDeviceChange: (device: MicDevice) => void;
  onEndSession: () => void;
}

export function SessionTransportControls({
  isPlaying,
  currentSection,
  sectionCount,
  onPlayPause,
  onPrevSection,
  onNextSection,
  participants,
  sessionSettings,
  onMusicDeviceChange,
  onMicDeviceChange,
  onEndSession,
}: SessionTransportControlsProps) {
  const theme = useTheme();

  return (
    <div
      data-testid="session-transport-controls"
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing.md};
        padding: ${spacing.sm} ${spacing.md};
        border: 1px solid ${theme.colors.border};
        border-radius: 8px;
        background: ${theme.colors.surface};
        margin-bottom: ${spacing.md};
        flex-wrap: wrap;
      `}
    >
      {/* Transport controls */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.sm};
        `}
      >
        <button
          type="button"
          onClick={onPrevSection}
          aria-label="Previous section"
          css={css`
            background: none;
            border: 1px solid ${theme.colors.border};
            border-radius: 6px;
            color: ${theme.colors.text};
            cursor: pointer;
            min-width: 36px;
            min-height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
              border-color: ${theme.colors.text};
            }
          `}
        >
          <SkipBack size={16} aria-hidden="true" fill="currentColor" />
        </button>

        <button
          type="button"
          onClick={onPlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          css={css`
            background: ${theme.colors.primary};
            border: none;
            border-radius: 50%;
            color: ${theme.colors.primaryText};
            cursor: pointer;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
              background: ${theme.colors.primaryHover};
            }
          `}
        >
          {isPlaying ? (
            <Pause size={18} aria-hidden="true" fill="currentColor" />
          ) : (
            <Play size={18} aria-hidden="true" fill="currentColor" />
          )}
        </button>

        <button
          type="button"
          onClick={onNextSection}
          aria-label="Next section"
          css={css`
            background: none;
            border: 1px solid ${theme.colors.border};
            border-radius: 6px;
            color: ${theme.colors.text};
            cursor: pointer;
            min-width: 36px;
            min-height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
              border-color: ${theme.colors.text};
            }
          `}
        >
          <SkipForward size={16} aria-hidden="true" fill="currentColor" />
        </button>

        <span
          css={css`
            font-size: ${fontSizes.sm};
            color: ${theme.colors.textSecondary};
            margin-left: ${spacing.xs};
          `}
        >
          Section {currentSection + 1} of {sectionCount}
        </span>
      </div>

      {/* Device settings */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.md};
          margin-left: auto;
          flex-wrap: wrap;
        `}
      >
        <label
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.xs};
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
          `}
        >
          Music:
          <select
            value={sessionSettings.musicDevice}
            onChange={(e) => onMusicDeviceChange(e.target.value as MusicDevice)}
            aria-label="Music plays on"
            css={css`
              font-size: ${fontSizes.xs};
              padding: 4px 8px;
              border-radius: 4px;
              border: 1px solid ${theme.colors.border};
              background: ${theme.colors.background};
              color: ${theme.colors.text};
              cursor: pointer;
            `}
          >
            <option value="driver">This device</option>
            <option value="follower">Teleprompter</option>
          </select>
        </label>

        <label
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.xs};
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
          `}
        >
          Mic:
          <select
            value={sessionSettings.micDevice}
            onChange={(e) => onMicDeviceChange(e.target.value as MicDevice)}
            aria-label="Mic listens on"
            css={css`
              font-size: ${fontSizes.xs};
              padding: 4px 8px;
              border-radius: 4px;
              border: 1px solid ${theme.colors.border};
              background: ${theme.colors.background};
              color: ${theme.colors.text};
              cursor: pointer;
            `}
          >
            <option value="driver">This device</option>
            <option value="follower">Teleprompter</option>
            <option value="off">Off</option>
          </select>
        </label>

        <span
          css={css`
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
          `}
        >
          {participants} device{participants !== 1 ? 's' : ''}
        </span>

        <button
          type="button"
          onClick={onEndSession}
          css={css`
            font-size: ${fontSizes.xs};
            padding: 4px ${spacing.sm};
            border-radius: 4px;
            border: 1px solid ${theme.colors.border};
            background: transparent;
            color: ${theme.colors.textSecondary};
            cursor: pointer;
            min-height: 28px;

            &:hover {
              border-color: ${theme.colors.text};
              color: ${theme.colors.text};
            }
          `}
        >
          End Session
        </button>
      </div>
    </div>
  );
}
